//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import {
  ILike2BuyV2TikTokDefaultV1Config,
  Like2BuyV2TemplateConfig,
} from '@apis/like2buy-v2/like2BuyV2StoreModel';
import { ILike2BuyCustomizations } from '@like2buy-v2/like2BuyV2UIUtils';
import { ITikTokLike2BuyCustomizations } from '@tiktok/redux/like2buy/customizations/tiktokLike2BuyCustomizationsModel';
import { SupportedImageMimeTypes } from '@utils/mimeTypes';

export const UPLOAD_BYTES_LIMIT = 5 * 1024 * 1024; // 5mb
export const SUPPORTED_IMAGE_UPLOAD_FILE_TYPES: SupportedImageMimeTypes[] = [
  'image/jpeg',
  'image/png',
  'image/webp',
];

export const BANNER_CONSTANTS = {
  destinationUrl: {
    name: 'destination-url',
    placeholder: 'https://www.yoursite.com',
    title: 'Banner Link',
    titleHelpText:
      'This link will open when a user selects your banner image. Tracking parameters based on your settings will be added to the link.',
    validationErrorMessage: 'Must be a valid URL.',
  },
  bannerImageUpload: {
    // TODO [PD-223673]: Change once size is increased in backend
    fileSizeErrorMessage: 'Images must be smaller than 5MB.',
    imageUploadedMessage: 'Image uploaded',
    inputId: 'banner-image-upload-input',
    name: 'banner-image-upload',
    noImageUploadedMessage: 'No image uploaded',
    removeBannerTooltip: 'Remove your uploaded banner image.',
    title: 'Banner Image',
    titleHelpText:
      'We recommend a resolution of 1260x315 pixels (4:1 aspect ratio) for your banner. Images must be smaller than 5MB and one of these file types: .jpeg, .png, or .webp.',
    validationErrorMessage: 'Must add a banner image.',
  },
  bannerAltText: {
    name: 'banner-alt-text',
    title: 'Banner Alt Text (Optional)',
    titleHelpText:
      'Add descriptive alt text to provide users using screen readers with context for your banner image.',
    ctaLimit: 120,
  },
} as const;

export const CTA_CONSTANTS = {
  galleryHeader: {
    name: 'gallery-header-cta',
    placeholder: 'Select a post for more information',
    title: 'Homepage',
    subtitle: 'Default: Select a post for more information',
    ctaLimit: 60,
  },
  galleryMediaHover: {
    name: 'gallery-media-hover-cta',
    placeholder: 'More Info',
    title: 'Media Hover (Desktop)',
    subtitle: 'Default: More Info',
    ctaLimit: 40,
  },
  productsContainer: {
    name: 'products-container-cta',
    placeholder: 'Featured in post',
    title: 'Product Page',
    subtitle: 'Default: Featured in post',
    ctaLimit: 60,
  },
} as const;

export const EMAIL_CAPTURE_CONSTANTS = {
  cta: {
    name: 'email-capture-cta',
    placeholder: 'Sign up to stay connected!',
    title: 'Call to Action',
    subtitle: 'Default: Sign up to stay connected!',
    ctaLimit: 60,
  },
  termsAndConditionsRequired: {
    name: 'email-capture-terms-and-conditions-required',
    title: 'Require Opt-in to Email Capture',
    subtitle:
      "Require users to confirm they've read your terms and conditions.",
  },
  termsAndConditionsUrl: {
    missingErrorMessage: 'Must provide a valid URL.',
    name: 'email-capture-terms-and-conditions-url',
    placeholder: 'https://www.example.com',
    title: 'Terms and Conditions Link',
    titleHelpText:
      'Add a terms and conditions link to include on your email capture form.',
    validationErrorMessage: 'Must be a valid URL.',
  },
} as const;

export function getExceededCtaLimitWarning(limit: number) {
  return `Please enter ${limit} characters or less.`;
}

export const ORDERED_INPUT_NAMES: readonly (
  | (typeof EMAIL_CAPTURE_CONSTANTS)[keyof typeof EMAIL_CAPTURE_CONSTANTS]['name']
  | (typeof CTA_CONSTANTS)[keyof typeof CTA_CONSTANTS]['name']
  | (typeof BANNER_CONSTANTS)[keyof typeof BANNER_CONSTANTS]['name']
)[] = [
  EMAIL_CAPTURE_CONSTANTS.cta.name,
  EMAIL_CAPTURE_CONSTANTS.termsAndConditionsUrl.name,
  BANNER_CONSTANTS.destinationUrl.name,
  BANNER_CONSTANTS.bannerImageUpload.name,
  BANNER_CONSTANTS.bannerAltText.name,
  CTA_CONSTANTS.galleryHeader.name,
  CTA_CONSTANTS.galleryMediaHover.name,
  CTA_CONSTANTS.productsContainer.name,
] as const;

export function isTiktokLike2BuyV2Template(
  template: Like2BuyV2TemplateConfig,
): template is ILike2BuyV2TikTokDefaultV1Config {
  return template['@type'] === 'v1:tiktok-default';
}

export function isTiktokLike2BuyV2Customizations(
  template: Like2BuyV2TemplateConfig,
  customizations: ILike2BuyCustomizations,
): customizations is ITikTokLike2BuyCustomizations {
  return template['@type'] === 'v1:tiktok-default';
}
